import { useNavigate, useSearchParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  Controller,
  type Control,
  type FieldErrors,
  type Resolver,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Checkbox } from "primereact/checkbox";
import { Panel } from "primereact/panel";
import {
  BasicInfoFields,
  BookingInfoFields,
  ConsentFields,
  TagsField,
  SessionFields,
  PricePointFields,
} from "@/components/events/EventFormFields";
import {
  AdminCalendar,
  FormActions,
  FormField,
  ImagePoolField,
  ImageUploadField,
  RecurrencePicker,
} from "@/components/forms";
import { formStyles } from "@/components/forms/formStyles";
import { transformEventFormDataToPayload } from "@/lib/event-utils";
import {
  buildClonedEventFormValues,
  getEventFormAnchorDate,
  shiftEventFormDates,
} from "@/lib/event-form-values";
import { formatDateTime } from "@/lib/utils";
import {
  eventSchema,
  type BookableEventFormData,
  type NonBookableEventFormData,
  type EventFormData,
} from "@/lib/event-schemas";
import { handleApiError } from "@/lib/error-handling";
import {
  useAdminEvent,
  useAdminEventCreate,
  useAdminTagsList,
} from "@/lib/admin-api";
import { confirmAsync, showToast } from "@/lib/ui";

export function EventCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);

  const cloneFromId = searchParams.get("cloneFrom") ?? undefined;
  const cloneSourceQuery = useAdminEvent(cloneFromId);
  const cloneSource = cloneSourceQuery.data?.event ?? null;
  const [cloneApplied, setCloneApplied] = useState(false);
  const [cloneStartsAt, setCloneStartsAt] = useState<Date | null>(null);
  const isCloneMode = Boolean(cloneFromId);
  const isCloneLoading = isCloneMode && !cloneApplied;

  const tagsQuery = useAdminTagsList({
    page: 1,
    pageSize: 100,
    sortField: "name",
    sortOrder: 1,
  });
  const availableTags = useMemo(() => {
    const options = (tagsQuery.data?.data ?? []).map((tag) => ({
      label: tag.name,
      value: tag.name,
    }));
    const known = new Set(options.map((t) => t.value));
    for (const { tag } of cloneSource?.tags ?? []) {
      if (!known.has(tag.name)) {
        options.push({ label: tag.name, value: tag.name });
        known.add(tag.name);
      }
    }
    return options;
  }, [cloneSource?.tags, tagsQuery.data?.data]);

  const bookableDefaults: BookableEventFormData = useMemo(
    () => ({
      isBookable: true,
      eventKind: "STANDARD",
      isFeatured: false,
      isRecurring: false,
      rrule: "",
      imagePool: [],
      timezone: "Europe/London",
      audience: "OPEN",
      requiresConsentForm: false,
      requiresPhotoConsent: false,
      groupsEnabled: false,
      isPublished: false,
      isCancelled: false,
      sessions: [],
      pricePoints: [],
      tags: [],
      schedule: null,
      whatToBring: null,
      location: null,
      bookingClosesAt: null,
      imageUrl: null,
      title: "",
      description: "",
    }),
    [],
  );

  const nonBookableDefaults: NonBookableEventFormData = useMemo(() => {
    const startsAt = new Date();
    const endsAt = new Date(startsAt.getTime() + 60 * 60 * 1000);
    return {
      isBookable: false,
      eventKind: "STANDARD",
      isFeatured: false,
      isRecurring: false,
      rrule: "",
      timezone: "Europe/London",
      isPublished: false,
      isCancelled: false,
      groupsEnabled: false,
      tags: [],
      title: "",
      description: "",
      location: null,
      startsAt,
      endsAt,
    };
  }, []);

  const form = useForm<EventFormData>({
    // TypeScript can't properly narrow discriminated unions in resolver types
    resolver: zodResolver(eventSchema) as unknown as Resolver<EventFormData>,
    defaultValues: bookableDefaults,
    mode: "onSubmit",
  });

  // react-hook-form types don't narrow cleanly across discriminated unions.
  // We only use this control for bookable-only field arrays and watches.
  const bookableControl = form.control as unknown as Control<BookableEventFormData>;

  const createEventMutation = useAdminEventCreate();

  useEffect(() => {
    if (!isCloneMode || cloneApplied) return;

    if (cloneSourceQuery.isError) {
      setCloneApplied(true);
      showToast(toast, {
        severity: "error",
        summary: "Error",
        detail: "Failed to load the event to clone. Starting with a blank form.",
      });
      return;
    }

    if (!cloneSource) return;

    const clonedValues = buildClonedEventFormValues(cloneSource);
    form.reset(clonedValues);
    setCloneStartsAt(getEventFormAnchorDate(clonedValues));
    setCloneApplied(true);
  }, [cloneApplied, cloneSource, cloneSourceQuery.isError, form, isCloneMode]);

  const handleCloneStartChange = (next: Date) => {
    // Shift the current values (not the source) so manual edits made since
    // cloning are preserved while relative offsets stay intact.
    const deltaMs = cloneStartsAt ? next.getTime() - cloneStartsAt.getTime() : 0;
    if (deltaMs !== 0) {
      form.reset(shiftEventFormDates(form.getValues(), deltaMs));
    }
    setCloneStartsAt(next);
  };

  const bookableRegister = form.register as UseFormRegister<BookableEventFormData>;
  const bookableWatch = form.watch as UseFormWatch<BookableEventFormData>;
  const bookableSetValue = form.setValue as UseFormSetValue<BookableEventFormData>;

  const onInvalid = (errors: FieldErrors<EventFormData>) => {
    showToast(toast, {
      severity: "error",
      summary: "Validation Error",
      detail: "Please check the highlighted fields and fix all validation errors.",
    });

    // Find the first error field path by traversing the errors object
    const findFirstErrorFieldPath = (
      errors: FieldErrors<EventFormData>,
      path = "",
    ): string | null => {
      for (const [key, value] of Object.entries(errors)) {
        const currentPath = path ? `${path}.${key}` : key;
        
        if (value && typeof value === "object") {
          // Check if this is an error object with a message
          if ("message" in value && typeof value.message === "string") {
            return currentPath;
          }
          
          // Check if this is an array of errors (for field arrays)
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const item = value[i];
              if (item && typeof item === "object") {
                const nestedPath = findFirstErrorFieldPath(
                  { [i]: item } as FieldErrors<EventFormData>,
                  currentPath,
                );
                if (nestedPath) return nestedPath;
              }
            }
          } else {
            // Recursively check nested errors
            const nestedPath = findFirstErrorFieldPath(
              value as FieldErrors<EventFormData>,
              currentPath,
            );
            if (nestedPath) return nestedPath;
          }
        }
      }
      return null;
    };

    const firstErrorPath = findFirstErrorFieldPath(errors);
    
    // Scroll to the first error field after a short delay to ensure DOM is updated
    if (firstErrorPath) {
      setTimeout(() => {
        // Try to find the field element by name or id
        // React Hook Form uses name attributes for registered fields
        const fieldName = firstErrorPath.split(".").pop() || firstErrorPath;
        let errorElement: HTMLElement | null = null;
        
        // Try different selectors to find the field
        // For field arrays, React Hook Form uses the full path as the name/id
        const selectors = [
          `[name="${firstErrorPath}"]`,
          `[id="${firstErrorPath}"]`,
          // For field arrays, also try the last part of the path
          `[name="${fieldName}"]`,
          `[id="${fieldName}"]`,
          // For RelativeDatePicker, the id might be on a wrapper or the dropdown
          `#${firstErrorPath}`,
          `#${fieldName}`,
        ];
        
        for (const selector of selectors) {
          errorElement = document.querySelector(selector) as HTMLElement;
          if (errorElement) break;
        }
        
        // If still not found and it's a field array, try to find the parent container
        // and scroll to that (the error will be visible in the FormField)
        if (!errorElement && firstErrorPath.includes(".")) {
          // Try to find the price point container
          const pathParts = firstErrorPath.split(".");
          if (pathParts[0] === "pricePoints") {
            // Find the price point panel by looking for the field name in nearby elements
            const allFields = document.querySelectorAll(`[id*="${fieldName}"], [name*="${fieldName}"]`);
            if (allFields.length > 0) {
              // Find the closest parent container (the price point card)
              const pricePointCard = allFields[0].closest('[style*="border"]') as HTMLElement;
              if (pricePointCard) {
                errorElement = pricePointCard;
              } else {
                errorElement = allFields[0] as HTMLElement;
              }
            }
          }
        }
        
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          // Focus if the element supports it (input, textarea, select, etc.)
          if ("focus" in errorElement && typeof errorElement.focus === "function") {
            errorElement.focus();
          }
        } else {
          // Fallback: scroll to top of form
          const form = document.querySelector("form");
          if (form) {
            form.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }, 100);
    }
  };

  const isBookable = Boolean(
    useWatch({
      control: form.control,
      name: "isBookable",
    }),
  );
  const isRecurring = Boolean(
    useWatch({
      control: form.control,
      name: "isRecurring",
    }),
  );
  const rrule =
    useWatch({
      control: form.control,
      name: "rrule",
    }) ?? "";

  const imagePool =
    useWatch({
      control: bookableControl,
      name: "imagePool",
    }) ?? [];

  const imageUrl =
    useWatch({
      control: bookableControl,
      name: "imageUrl",
    }) ?? null;

  useEffect(() => {
    if (!isRecurring) {
      form.setValue("rrule", "", { shouldValidate: true });
      if (isBookable) {
        bookableSetValue("imagePool", [], { shouldValidate: true });
      }
      return;
    }

    if (rrule.trim() === "") {
      form.setValue("rrule", "FREQ=WEEKLY;COUNT=5", { shouldValidate: true });
    }
  }, [bookableSetValue, form, isBookable, isRecurring, rrule]);

  const {
    fields: sessionFields,
    append: appendSession,
    remove: removeSession,
  } = useFieldArray({
    control: bookableControl,
    name: "sessions",
  });

  const {
    fields: pricePointFields,
    append: appendPricePoint,
    remove: removePricePoint,
  } = useFieldArray({
    control: bookableControl,
    name: "pricePoints",
  });

  const executeSubmit = async (
    formData: EventFormData,
    isPublished: boolean,
  ) => {
    setLoading(true);
    try {
      const payload = transformEventFormDataToPayload({
        ...formData,
        isPublished,
      } satisfies EventFormData);

      let data: { count: number; eventIds: string[] };
      try {
        data = await createEventMutation.mutateAsync({ body: payload });
      } catch (apiError: unknown) {
        const { message: errorMessage, hasValidationErrors } = handleApiError<EventFormData>(
          apiError,
          form.setError,
        );

        showToast(toast, {
          severity: "error",
          summary: hasValidationErrors ? "Validation Error" : "Error",
          detail: errorMessage,
        });

        if (!hasValidationErrors) {
          throw new Error(errorMessage);
        }
        return;
      }

      showToast(toast, {
        severity: "success",
        summary: "Success",
        detail:
          data.count === 1
            ? "Event created successfully"
            : "Series created successfully",
      });

      if (data.eventIds.length === 1) {
        void navigate(`/events/${data.eventIds[0]}`);
      } else {
        void navigate("/events");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create event";
      showToast(toast, {
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData: EventFormData) => {
    const publishNow = await confirmAsync({
      message: isRecurring
        ? "Do you want to publish this series immediately?"
        : "Do you want to publish this event immediately?",
      header: isRecurring ? "Publish Series" : "Publish Event",
      icon: "pi pi-question-circle",
      acceptLabel: "Publish Now",
      rejectLabel: "Save as Draft",
      defaultFocus: "accept",
    });
    await executeSubmit(formData, publishNow);
  };

  const switchBookableMode = (nextIsBookable: boolean) => {
    if (nextIsBookable === isBookable) return;

    const prev = form.getValues();

    if (nextIsBookable) {
      form.reset({
        ...bookableDefaults,
        title: prev.title,
        description: prev.description,
        timezone: prev.timezone,
        location: prev.location ?? null,
        isPublished: prev.isPublished ?? false,
        isCancelled: prev.isCancelled ?? false,
        isRecurring: prev.isRecurring ?? false,
        rrule: prev.rrule ?? "",
        tags: prev.tags ?? [],
      });
    } else {
      const now = new Date();
      const startsAt = now;
      const endsAt = new Date(now.getTime() + 60 * 60 * 1000);
      form.reset({
        ...nonBookableDefaults,
        title: prev.title,
        description: prev.description,
        timezone: prev.timezone,
        location: prev.location ?? null,
        isPublished: prev.isPublished ?? false,
        isCancelled: prev.isCancelled ?? false,
        isRecurring: prev.isRecurring ?? false,
        rrule: prev.rrule ?? "",
        tags: prev.tags ?? [],
        startsAt,
        endsAt,
      });
    }
  };

  return (
    <div className="card">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex flex-column gap-3 mb-4">
        <div className="flex align-items-center gap-3 mb-4">
          <BackButton />
          <h1 className="text-2xl font-bold m-0">
            {isCloneMode ? "Clone Event" : "Create Event"}
          </h1>
        </div>
      </div>

      {isCloneLoading ? (
        <div className="text-center p-4">Loading...</div>
      ) : (
        <form
          onSubmit={(e) => {
            // TypeScript can't properly narrow discriminated unions in handleSubmit types
            void (form.handleSubmit(onSubmit, onInvalid) as unknown as (e: React.FormEvent<HTMLFormElement>) => Promise<void>)(e);
          }}
          className="flex flex-column gap-2"
        >
          {cloneSource ? (
            <Panel header="Clone Settings">
              <div className="flex flex-column gap-3">
              <div>
                Cloned from <strong>{cloneSource.title}</strong>
                {cloneSource.startsAt
                  ? ` (originally ${formatDateTime(cloneSource.startsAt, cloneSource.timezone)})`
                  : null}
              </div>
                <FormField
                  id="cloneStartsAt"
                  label="New start date & time"
                  helperText="Changing this shifts every session, booking deadline and price point date by the same offset, keeping their relative timing."
                >
                  <AdminCalendar
                    inputId="cloneStartsAt"
                    value={cloneStartsAt}
                    onChange={(e) => {
                      if (e.value instanceof Date) handleCloneStartChange(e.value);
                    }}
                    showTime
                    showIcon
                    className="w-full"
                    inputStyle={formStyles.input}
                    inputClassName="w-full"
                  />
                </FormField>
              </div>
            </Panel>
          ) : null}

          <Panel header="Event Settings">
            <div className="flex flex-column gap-3">
              <div className="flex flex-wrap gap-4 align-items-center">
                <div className="flex align-items-center gap-2">
                  <Controller
                    control={form.control}
                    name="isBookable"
                    render={({ field }) => (
                      <Checkbox
                        inputId="isBookable"
                        checked={Boolean(field.value)}
                        onChange={(e) => {
                          const nextValue = Boolean(e.checked);
                          field.onChange(nextValue);
                          switchBookableMode(nextValue);
                        }}
                      />
                    )}
                  />
                  <label htmlFor="isBookable">Bookable event</label>
                </div>
                <div className="flex align-items-center gap-2">
                  <Checkbox
                    inputId="isRecurring"
                    checked={isRecurring}
                    onChange={(e) => {
                      const next = Boolean(e.checked);
                      form.setValue("isRecurring", next, { shouldValidate: true });
                    }}
                  />
                  <label htmlFor="isRecurring">Recurring event</label>
                </div>
              </div>

              {isRecurring ? (
                <RecurrencePicker
                  id="rrule"
                  label="Recurrence"
                  value={rrule}
                  onChange={(val) => {
                    form.setValue("rrule", val, { shouldValidate: true });
                  }}
                  error={
                    form.formState.errors.rrule?.message
                  }
                  helperText="The series will start on the first event start time."
                />
              ) : null}
            </div>
          </Panel>

          {isBookable ? (
            <BasicInfoFields
              register={form.register as unknown as UseFormRegister<BookableEventFormData>}
              errors={form.formState.errors as unknown as FieldErrors<BookableEventFormData>}
              control={form.control as unknown as Control<BookableEventFormData>}
              isBookable={true}
            />
          ) : (
            <BasicInfoFields
              register={form.register as unknown as UseFormRegister<NonBookableEventFormData>}
              errors={form.formState.errors as unknown as FieldErrors<NonBookableEventFormData>}
              control={form.control as unknown as Control<NonBookableEventFormData>}
              watch={form.watch as unknown as UseFormWatch<NonBookableEventFormData>}
              setValue={form.setValue as unknown as UseFormSetValue<NonBookableEventFormData>}
              isBookable={false}
            />
          )}

          {isBookable ? (
            <Panel header="Event Images">
              {isRecurring ? (
                <ImagePoolField
                  id="imagePool"
                  label="Image Pool"
                  value={imagePool}
                  onChange={(urls) =>
                    bookableSetValue("imagePool", urls, {
                      shouldValidate: true,
                    })
                  }
                  error={
                    (form.formState.errors as FieldErrors<BookableEventFormData>).imagePool
                      ?.message
                  }
                  helperText="Upload images to be rotated through the series. If fewer images are provided than events, they will be reused."
                />
              ) : (
                <ImageUploadField
                  id="imageUrl"
                  label="Event Image"
                  value={imageUrl}
                  onChange={(url) =>
                    bookableSetValue("imageUrl", url, {
                      shouldValidate: true,
                    })
                  }
                  error={
                    (form.formState.errors as FieldErrors<BookableEventFormData>).imageUrl
                      ?.message
                  }
                />
              )}
            </Panel>
          ) : null}

          {isBookable ? (
            <SessionFields
              fields={sessionFields}
              errors={form.formState.errors as FieldErrors<BookableEventFormData>}
              control={bookableControl}
              watch={bookableWatch}
              setValue={bookableSetValue}
              register={bookableRegister}
              append={appendSession}
              remove={removeSession}
            />
          ) : null}

          {isBookable ? (
            <PricePointFields
              fields={pricePointFields}
              errors={form.formState.errors as FieldErrors<BookableEventFormData>}
              watch={bookableWatch}
              setValue={bookableSetValue}
              register={bookableRegister}
              append={appendPricePoint}
              remove={removePricePoint}
            />
          ) : null}

          {isBookable ? (
            <BookingInfoFields
              control={bookableControl}
              errors={form.formState.errors as FieldErrors<BookableEventFormData>}
            />
          ) : null}

          {isBookable ? <ConsentFields control={bookableControl} /> : null}

          {isBookable ? (
            <TagsField
              control={form.control}
              errors={form.formState.errors as FieldErrors<BookableEventFormData>}
              availableTags={availableTags}
            />
          ) : null}

          <FormActions
            primaryLabel={isRecurring ? "Create Series" : "Create Event"}
            onPrimary={() => {
              // TypeScript can't properly narrow discriminated unions in handleSubmit types
              void (form.handleSubmit(onSubmit, onInvalid) as unknown as () => Promise<void>)();
            }}
            primarySubmit
            primaryLoading={loading}
            secondaryLabel="Cancel"
            onSecondary={() => {
              void navigate("/events");
            }}
          />
        </form>
      )}
    </div>
  );
}
